/**
 * Audit context store.
 *
 * A plain module-level singleton that bridges live React state (the resolved
 * user alias / numeric user_id and the currently selected team) into the static
 * Amplify GraphQL header function, which cannot read React state directly.
 *
 * React writes into the store via `setAuditUser` / `setAuditTeam`; the shared
 * Amplify header builder reads it via `getAuditContext` and serializes it with
 * `buildAuditContextHeaderValue`. See design "Components and Interfaces >
 * 1. Frontend — Audit context store".
 *
 * Requirements: 1.1, 1.3, 1.4, 1.5
 */

/** Sentinel used for an unresolved alias / team name (Req 1.3, 1.5). */
const UNKNOWN = "unknown";
/** Sentinel used for an unresolved numeric id (Req 1.3, 1.4). */
const UNRESOLVED_ID = -1;

export type AuditContext = {
  alias: string; // "unknown" until an authenticated session resolves
  user_id: number; // -1 until resolved from the backend getUser/createUser result
  team_id: number; // -1 until a real team (>0) is selected
  team_name: string; // "unknown" until resolved
};

/**
 * Module singleton, initialized to the sentinel defaults so that operations
 * issued before the user/team resolve still carry a well-formed context
 * (Req 1.3, 1.4, 1.5).
 */
const auditContext: AuditContext = {
  alias: UNKNOWN,
  user_id: UNRESOLVED_ID,
  team_id: UNRESOLVED_ID,
  team_name: UNKNOWN,
};

/**
 * Record the acting user. Called from React once the authenticated identity and
 * numeric user_id resolve.
 * - alias null or "" maps to "unknown" (Req 1.5)
 * - userId null or undefined maps to -1 (Req 1.4)
 */
export function setAuditUser(
  alias: string | null,
  userId: number | null
): void {
  auditContext.alias = alias ? alias : UNKNOWN;
  auditContext.user_id =
    userId === null || userId === undefined ? UNRESOLVED_ID : userId;
}

/**
 * Record the current team. Called from React after a team is resolved/selected.
 * - teamId null, undefined, or <= 0 maps to -1 (Req 1.3)
 * - teamName null, undefined, or "" maps to "unknown" (Req 1.3)
 */
export function setAuditTeam(
  teamId: number | null,
  teamName: string | null
): void {
  auditContext.team_id =
    teamId === null || teamId === undefined || teamId <= 0
      ? UNRESOLVED_ID
      : teamId;
  auditContext.team_name = teamName ? teamName : UNKNOWN;
}

/** Return a snapshot of the current audit context (read by the header builder). */
export function getAuditContext(): AuditContext {
  return { ...auditContext };
}

/**
 * Pure builder for the `X-Audit-Context` header value.
 *
 * Returns `base64url(JSON.stringify({ alias, user_id, team_id, team_name, ts }))`
 * where `ts` is an ISO-8601 UTC timestamp with an explicit "Z" designator
 * (Req 1.1). `now` is injectable so the timestamp is deterministic under test.
 */
export function buildAuditContextHeaderValue(now?: () => Date): string {
  const ctx = getAuditContext();
  const ts = (now ? now() : new Date()).toISOString();
  const payload = {
    alias: ctx.alias,
    user_id: ctx.user_id,
    team_id: ctx.team_id,
    team_name: ctx.team_name,
    ts,
  };
  return base64UrlEncode(JSON.stringify(payload));
}

/**
 * Encode a string as base64url (RFC 4648 §5), UTF-8 and HTTP-header safe.
 *
 * TextEncoder produces the UTF-8 byte sequence so Unicode team names survive
 * intact; the bytes are packed into a binary string for `btoa`, then mapped to
 * the URL-safe alphabet with padding stripped so the result contains no `+`,
 * `/`, or `=` that would require escaping in a header value.
 */
function base64UrlEncode(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}
